/**
 * Audit log contract (KAN-34 / KAN-46).
 *
 * "All create/update/delete actions on financial data are captured in an audit
 * log." The log is append-only and records both the actor and the before/after
 * state, so a disputed figure can be traced to who changed it and what it was.
 */
/** The entity kinds considered financial data for audit purposes. */
export const AUDITED_ENTITIES = [
    'client',
    'attorney',
    'matter',
    'matter_event',
    'time_entry',
    'invoice',
    'client_payment',
    'client_refund',
    'trust_ledger_entry',
    'vendor',
    'vendor_payment',
    /**
     * The chart of accounts is audited for the same reason vendors are: it is
     * synced reference data that every figure downstream is expressed against. An
     * account being renamed, reclassified or deactivated in QuickBooks silently
     * moves money between budget categories here, and "when did Rent stop being
     * an Expense" is exactly the question a variance nobody can explain leads to.
     */
    'gl_account',
    'budget',
    /**
     * A recurring obligation is a **decision Finance took** about what the firm
     * owes and when — the same shape of fact as an alert threshold, and audited
     * for the same reason. "Who changed the payroll figure, and when" is the
     * first question a forecast that turned out to be wrong leads to, and the
     * obligation table carries no history of its own.
     */
    'recurring_obligation',
    /**
     * The scenario, and not the lines or adjustments inside it. A scenario is a
     * decision about what the firm might do; a line is one figure within that
     * decision, and logging each of a hundred copied rows on every clone would
     * bury the decision under its own mechanics. The same split alerts and their
     * deliveries take.
     */
    'forecast_scenario',
    /**
     * The draft, and not the report whose figures it carries. The report is a
     * machine recomputing a derivation; every transition on the draft is a person
     * deciding something, and KAN-80 requires the send to be recorded with the
     * approver's identity by name.
     */
    'report_draft',
    /**
     * A commitment is a person's assertion about a conversation with a client,
     * not a machine's derivation — the same reason a refund's approval is
     * audited. "Who said this client would pay on the 14th" is exactly the
     * question asked on the 15th.
     */
    'payment_commitment',
    'billing_exception',
    /**
     * A matter's agreed rate table (KAN-72).
     *
     * Synced reference data, audited for the reason the chart of accounts is: it
     * is what every rate-mismatch finding is expressed against, so a rate changed
     * in Clio is the explanation for a queue that filled overnight. The table
     * carries no history of its own — Clio does not keep rate history either —
     * which makes the audit log the only place "when did this become 550?" can
     * be answered from.
     */
    'matter_rate',
    /**
     * The thresholds the exception rules read. A decision Finance took about what
     * the firm is told, not a machine's derivation — lowering the minimum
     * description length changes which entries are flagged, and "who moved it"
     * is the first question a queue that suddenly emptied leads to.
     */
    'billing_rule_setting',
    /**
     * The firm's weekly retainer goal. A target Finance set, read on the weekly
     * dashboard beside what came in, so "who moved the goal, and when" has to be
     * answerable for the same reason a threshold's does.
     */
    'firm_goal_setting',
    'user',
    'integration_connection',
    /**
     * Alert rules and alerts are audited; deliveries and per-person notification
     * preferences are not.
     *
     * The split is the one this codebase already applies to forecast snapshots. A
     * threshold change is a **decision Finance took** that alters which
     * exceptions the firm is told about, and "who lowered the trust floor, and
     * when" is a question somebody will ask after an alert failed to fire. An
     * acknowledgement is the same shape of fact. A delivery attempt is a machine
     * writing what it just did — thousands of rows that would bury the decisions
     * beside them — and the delivery table *is* that record already.
     */
    'alert_rule',
    'alert',
];
//# sourceMappingURL=audit.js.map